import { useRef, useState } from "react";
import { api } from "@/lib/api";
import { Field, Section, TagInput, LANGUAGE_CODES } from "./_shared";

const CURRENCIES = ["EUR", "USD", "GBP", "ALL", "CHF", "TRY"];
const TIMEZONES = ["Europe/Tirane", "Europe/Rome", "Europe/Athens", "Europe/Vienna", "Europe/London", "UTC"];

/**
 * File + URL combo for images. Uploads via POST /api/uploads/image and stores
 * the returned relative URL in the wizard data (no base64 bloat).
 */
function ImageInput({ value, onChange, placeholder, testId, kind }) {
    const fileRef = useRef(null);
    const [busy, setBusy] = useState(false);
    const [progress, setProgress] = useState(0);

    const handleFile = async (file) => {
        if (!file) return;
        // Client-side pre-flight — match the backend allow-list exactly so
        // we don't waste a multipart round-trip on obviously wrong files.
        const ALLOWED = ["image/png", "image/jpeg", "image/webp", "image/gif", "image/svg+xml"];
        if (!ALLOWED.includes(file.type)) {
            alert(`Format i pambështetur: ${file.type || "?"}. Lejohen: PNG, JPG, WebP, GIF, SVG.`);
            return;
        }
        if (file.size > 5 * 1024 * 1024) {
            alert(`Imazhi është ${(file.size / 1024 / 1024).toFixed(1)} MB. Maksimumi 5 MB.`);
            return;
        }
        setBusy(true);
        setProgress(0);
        try {
            const fd = new FormData();
            fd.append("file", file);
            // NOTE: DO NOT set Content-Type manually. Axios/browser must add
            // `multipart/form-data; boundary=…` themselves — an explicit
            // header without boundary breaks FastAPI's multipart parser and
            // was the exact cause of the earlier "Storage backend error".
            const { data } = await api.post(`/uploads/image${kind ? `?kind=${kind}` : ""}`, fd, {
                onUploadProgress: (e) => {
                    if (!e.total) return;
                    setProgress(Math.round((e.loaded / e.total) * 100));
                },
            });
            if (data?.url) onChange(data.url);
        } catch (e) {
            const d = e?.response?.data?.detail;
            const msg = typeof d === "object" && d?.message ? d.message :
                        typeof d === "string" ? d :
                        (e.message || "Ngarkimi dështoi");
            alert(`Ngarkimi dështoi: ${msg}`);
        } finally {
            setBusy(false);
            setProgress(0);
        }
    };

    const isUploadedUrl = typeof value === "string" && value.startsWith("/api/uploads/");
    const previewUrl = isUploadedUrl
        ? `${process.env.REACT_APP_BACKEND_URL || ""}${value}`
        : value;

    return (
        <div className="space-y-2">
            <div className="flex items-center gap-2">
                <input
                    className="akm-input flex-1"
                    value={isUploadedUrl ? "" : (value || "")}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={placeholder}
                    disabled={isUploadedUrl || busy}
                    data-testid={testId}
                />
                <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    disabled={busy}
                    className="akm-btn-ghost whitespace-nowrap text-[12px]"
                    data-testid={`${testId}-upload-button`}
                >
                    {busy ? (progress > 0 ? `${progress}%` : "Duke ngarkuar…") : "Ngarko nga PC"}
                </button>
                <input
                    ref={fileRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml"
                    className="hidden"
                    onChange={(e) => handleFile(e.target.files?.[0])}
                />
            </div>
            {busy && progress > 0 && (
                <div className="h-1 rounded bg-muted overflow-hidden" data-testid={`${testId}-progress`}>
                    <div className="h-1 bg-primary transition-[width]" style={{ width: `${progress}%` }} />
                </div>
            )}
            {value && (
                <div className="flex items-center gap-2 rounded-md border border-border bg-muted/30 px-2 py-1.5">
                    <img src={previewUrl} alt="preview" className="h-10 w-10 rounded object-cover" />
                    <span className="flex-1 truncate text-[11px] text-muted-foreground">
                        {isUploadedUrl ? "Skedar i ngarkuar në server" : value}
                    </span>
                    <button
                        type="button"
                        onClick={() => { onChange(""); if (fileRef.current) fileRef.current.value = ""; }}
                        className="text-[11px] text-red-600 hover:underline"
                    >
                        Hiq
                    </button>
                </div>
            )}
        </div>
    );
}

export default function Step1General({ data, update }) {
    return (
        <>
            <Section title="Identiteti i hotelit">
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    <Field label="Emri i hotelit" required>
                        <input className="akm-input" value={data.hotel_name} onChange={(e) => update({ hotel_name: e.target.value })} placeholder="p.sh. Akomoda Suites" data-testid="wizard-hotel-name" />
                    </Field>
                    <Field label="Emri ligjor i kompanisë">
                        <input className="akm-input" value={data.company_name} onChange={(e) => update({ company_name: e.target.value })} placeholder="p.sh. Akomoda Hospitality SHPK" />
                    </Field>
                    <Field label="Logo" hint="URL HTTPS ose ngarkim nga PC (max 5 MB)">
                        <ImageInput
                            value={data.logo_url}
                            onChange={(v) => update({ logo_url: v })}
                            placeholder="https://…/logo.png"
                            testId="wizard-logo-url"
                            kind="logo"
                        />
                    </Field>
                    <Field label="Imazhi i kopertinës" hint="URL HTTPS ose ngarkim nga PC (max 5 MB)">
                        <ImageInput
                            value={data.cover_image_url}
                            onChange={(v) => update({ cover_image_url: v })}
                            placeholder="https://…/cover.jpg"
                            testId="wizard-cover-url"
                            kind="cover"
                        />
                    </Field>
                </div>
            </Section>

            <Section title="Vendndodhja">
                <Field label="Adresa">
                    <input className="akm-input" value={data.address} onChange={(e) => update({ address: e.target.value })} placeholder="Rruga, numri" />
                </Field>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                    <Field label="Qyteti"><input className="akm-input" value={data.city} onChange={(e) => update({ city: e.target.value })} /></Field>
                    <Field label="Shteti"><input className="akm-input" value={data.country} onChange={(e) => update({ country: e.target.value })} /></Field>
                    <Field label="Zona kohore">
                        <select className="akm-input" value={data.timezone} onChange={(e) => update({ timezone: e.target.value })}>
                            {TIMEZONES.map((t) => <option key={t}>{t}</option>)}
                        </select>
                    </Field>
                </div>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    <Field label="Monedha">
                        <select className="akm-input" value={data.currency} onChange={(e) => update({ currency: e.target.value })}>
                            {CURRENCIES.map((c) => <option key={c}>{c}</option>)}
                        </select>
                    </Field>
                    <Field label="Gjuhët" hint="Vetëm kode ISO (p.sh. sq, en, it, de, fr)">
                        <TagInput
                            value={data.languages}
                            onChange={(v) => update({ languages: v })}
                            placeholder="sq, en, it…"
                            restrictTo={LANGUAGE_CODES}
                            normalize={(v) => v.toLowerCase().slice(0, 2)}
                        />
                    </Field>
                </div>
            </Section>

            <Section title="Kontakti">
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    <Field label="Email-i i kontaktit" required>
                        <input type="email" className="akm-input" value={data.contact_email} onChange={(e) => update({ contact_email: e.target.value })} placeholder="info@hoteli.com" data-testid="wizard-contact-email" />
                    </Field>
                    <Field label="Telefoni"><input className="akm-input" value={data.phone} onChange={(e) => update({ phone: e.target.value })} placeholder="+355 …" /></Field>
                    <Field label="Faqja web"><input className="akm-input" value={data.website} onChange={(e) => update({ website: e.target.value })} placeholder="https://hoteli.com" /></Field>
                </div>
            </Section>

            <Section title="Informacione fiskale" description="NUIS dhe të dhëna tatimore që përdoren në fatura.">
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    <Field label="NUIS / Numër tatimor"><input className="akm-input" value={data.vat_number} onChange={(e) => update({ vat_number: e.target.value })} placeholder="K12345678L" /></Field>
                    <Field label="Info fiskale" hint="Numër regjistrimi, regjim fiskal, etj."><input className="akm-input" value={data.fiscal_info} onChange={(e) => update({ fiscal_info: e.target.value })} /></Field>
                </div>
            </Section>
        </>
    );
}
